import type { ReactNode } from 'react';
import { Button } from './ui.tsx';

/**
 * The Properties panel, in the shape a Windows user expects: an icon, the
 * name, then a table of facts.
 *
 * Deliberately generic. Folders, files and spreadsheets are different records
 * with different fields, but the panel that describes them should not be three
 * near-identical components that drift apart.
 */

export interface PropertyRow {
  label: string;
  value: ReactNode;
  /** Renders in the monospace/tabular style — sizes, dates, counts. */
  numeric?: boolean;
}

export interface PropertiesContent {
  name: string;
  kind: string;
  icon: ReactNode;
  rows: readonly PropertyRow[];
}

export function PropertiesPanel({
  content,
  onClose,
}: {
  content: PropertiesContent;
  onClose(): void;
}) {
  return (
    <div>
      <div className="flex items-center gap-3 border-b border-border px-5 py-4">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sunken text-blue-600">
          {content.icon}
        </span>
        <div className="min-w-0">
          <h2 className="truncate text-h3 text-ink" title={content.name}>
            {content.name}
          </h2>
          <p className="text-meta text-muted">{content.kind}</p>
        </div>
      </div>

      <dl className="max-h-[60vh] overflow-y-auto px-5 py-3">
        {content.rows.map((row) => (
          <div key={row.label} className="flex gap-4 border-b border-border py-2 last:border-0">
            <dt className="w-36 shrink-0 text-meta text-muted">{row.label}</dt>
            <dd
              className="min-w-0 flex-1 break-words text-body-sm text-ink"
              {...(row.numeric ? { 'data-numeric': true } : {})}
            >
              {row.value}
            </dd>
          </div>
        ))}
      </dl>

      <div className="flex justify-end border-t border-border px-5 py-3">
        <Button variant="primary" data-autofocus="" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
}
